import React, { useState, useEffect } from 'react';
import { Modal, Spinner, Button } from 'react-bootstrap';
import { plansList, addonsList, createCheckoutSession, getCompanySubscription, updateSubscriptionAddons, updateSubscriptionPlan } from '../api/subscription';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

const SubscriptionPlansModal = ({ show, onClose, companySubscription: initialSubscription = null }) => {
  const [plans, setPlans] = useState([]);
  const [addons, setAddons] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [viewMode, setViewMode] = useState('plans'); // 'plans' or 'details'
  const [subscription, setSubscription] = useState(initialSubscription);
  const [managingAddons, setManagingAddons] = useState(false);
  const [selectedAddonsToManage, setSelectedAddonsToManage] = useState([]);
  const [updatingSubscription, setUpdatingSubscription] = useState(false);
  
  const companyId = useSelector((store) => store.main.company_id);
  const authCompanyId = useSelector((store) => store.auth?.company_id);
  const company_id = companyId || authCompanyId;

  useEffect(() => {
    if (show) {
      if (initialSubscription) {
        setViewMode('details');
        setSubscription(initialSubscription);
      } else {
        setViewMode('plans');
        setSubscription(null);
        fetchPlans();
      }
    }
  }, [show, initialSubscription]);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const [plansRes, addonsRes] = await Promise.all([
        plansList({ page: 1, limit: 100 }),
        addonsList({ page: 1, limit: 100 })
      ]);
      
      if (plansRes?.code === 1) {
        let allPlans = plansRes.data?.plans || [];
        
        // Filter plans based on company subscription
        if (company_id) {
          // Company has subscription - show only company-specific plans
          const companyPlans = allPlans.filter(plan => 
            plan.plan_type === 'custom' && plan.company_id === company_id
          );
          
          if (companyPlans.length > 0) {
            // Show company-specific plans
            setPlans(companyPlans);
            console.log('Showing company-specific plans:', companyPlans);
            
            // Auto-select if only one plan
            if (companyPlans.length === 1) {
              setSelectedPlan(companyPlans[0]);
              console.log('Auto-selected company plan:', companyPlans[0]);
            }
          } else {
            // No company-specific plans, show general plans
            const generalPlans = allPlans.filter(plan => plan.plan_type === 'general');
            setPlans(generalPlans);
            console.log('No company-specific plans found, showing general plans:', generalPlans);
            
            // Auto-select if only one plan
            if (generalPlans.length === 1) {
              setSelectedPlan(generalPlans[0]);
              console.log('Auto-selected general plan:', generalPlans[0]);
            }
          }
        } else {
          // No company subscription - show all general plans
          const generalPlans = allPlans.filter(plan => plan.plan_type === 'general');
          setPlans(generalPlans);
          console.log('No company ID, showing general plans:', generalPlans);
          
          // Auto-select if only one plan
          if (generalPlans.length === 1) {
            setSelectedPlan(generalPlans[0]);
            console.log('Auto-selected general plan:', generalPlans[0]);
          }
        }
      }
      if (addonsRes?.code === 1) {
        setAddons(addonsRes.data?.addons || []);
      }
    } catch (err) {
      console.error('Error fetching plans:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddonToggle = (addonId) => {
    setSelectedAddons(prev => {
      const newSelection = prev.includes(addonId) 
        ? prev.filter(id => id !== addonId)
        : [...prev, addonId];
      console.log('Addon toggled:', addonId, 'New selection:', newSelection);
      return newSelection;
    });
  };

  const handlePlanSelection = (plan) => {
    setSelectedPlan(plan);
    // Clear addon selections when switching plans
    setSelectedAddons([]);
  };

  const switchToPlansView = () => {
    setViewMode('plans');
    fetchPlans();
  };

  const toggleAddonManagement = async () => {
    setManagingAddons(!managingAddons);
    if (!managingAddons) {
      // Fetch addons if not already loaded
      if (addons.length === 0) {
        await fetchPlans(); // This fetches both plans and addons
      }
      // Initialize selected addons with current subscription addons
      const currentAddonIds = subscription.addons?.map(addon => addon.addon_id) || [];
      setSelectedAddonsToManage(currentAddonIds);
    }
  };

  const handleAddonToggleForSubscription = (addonId) => {
    setSelectedAddonsToManage(prev => {
      if (prev.includes(addonId)) {
        return prev.filter(id => id !== addonId);
      } else {
        return [...prev, addonId];
      }
    });
  };

  const handleUpdateSubscriptionAddons = async () => {
    if (!company_id) {
      toast.error('Company ID not found');
      return;
    }

    setUpdatingSubscription(true);
    try {
      // Get current addon IDs
      const currentAddonIds = subscription.addons?.map(addon => addon.addon_id) || [];
      
      // Find addons to add and remove
      const addonsToAdd = selectedAddonsToManage.filter(id => !currentAddonIds.includes(id));
      const addonsToRemove = currentAddonIds.filter(id => !selectedAddonsToManage.includes(id));

      console.log('Current addons:', currentAddonIds);
      console.log('Selected addons:', selectedAddonsToManage);
      console.log('Addons to add:', addonsToAdd);
      console.log('Addons to remove:', addonsToRemove);

      if (addonsToAdd.length === 0 && addonsToRemove.length === 0) {
        toast.info('No changes to addons');
        return;
      }

      // Call API to update subscription
      const result = await updateSubscriptionAddons({
        company_id: company_id,
        subscription_id: subscription.stripe_subscription_id,
        addons_to_add: addonsToAdd,
        addons_to_remove: addonsToRemove
      });
      
      if (result.code === 1) {
        toast.success('Subscription addons updated successfully');
        // Refresh subscription data
        const updatedSubscription = await getCompanySubscription(company_id);
        if (updatedSubscription?.code === 1) {
          setSubscription(updatedSubscription.data);
        }
        setManagingAddons(false);
      } else {
        toast.error(result.message || 'Failed to update subscription addons');
      }
    } catch (error) {
      console.error('Error updating subscription addons:', error);
      toast.error('Error updating subscription addons');
    } finally {
      setUpdatingSubscription(false);
    }
  };

  const isChangingExistingPlan = Boolean(subscription?.stripe_subscription_id && viewMode === 'plans');

  const handlePurchase = async () => {
    if (!selectedPlan) {
      toast.error('Please select a plan first');
      return;
    }

    if (!company_id) {
      toast.error('Company ID not found');
      return;
    }

    if (isChangingExistingPlan) {
      const currentPlanId = subscription.plan_id ?? subscription.plan?.id;
      if (currentPlanId != null && Number(currentPlanId) === Number(selectedPlan.id)) {
        toast.info('This is already your current plan');
        return;
      }
    }

    setPurchasing(true);
    try {
      if (isChangingExistingPlan) {
        const response = await updateSubscriptionPlan({
          company_id,
          plan_id: selectedPlan.id,
          stripe_subscription_id: subscription.stripe_subscription_id
        });

        if (response?.code === 1) {
          toast.success('Plan updated successfully');
          const updatedSubscription = await getCompanySubscription(company_id);
          if (updatedSubscription?.code === 1) {
            setSubscription(updatedSubscription.data);
          }
          setViewMode('details');
        } else {
          toast.error(response?.message || 'Failed to update plan');
        }
        return;
      }

      const payload = {
        plan_id: selectedPlan.id,
        addon_ids: selectedAddons,
        company_id: company_id
      };

      console.log('Purchase payload:', payload);
      console.log('Selected addons count:', selectedAddons.length);
      console.log('Selected addon IDs:', selectedAddons);

      const response = await createCheckoutSession(payload);
      
      if (response?.code === 1 && response?.data?.url) {
        window.location.href = response.data.url;
      } else {
        toast.error(response?.message || 'Failed to create checkout session');
      }
    } catch (error) {
      console.error('Error processing subscription:', error);
      toast.error('An error occurred while processing your request');
    } finally {
      setPurchasing(false);
    }
  };

  const scrollPlans = (direction) => {
    const container = document.getElementById('plans-scroll-container');
    if (container) {
      const scrollAmount = 350;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {viewMode === 'details'
            ? 'Your Subscription Details'
            : isChangingExistingPlan
              ? 'Change subscription plan'
              : 'Choose Your Subscription Plan'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ minHeight: '550px', paddingTop: '20px' }}>
        {viewMode === 'details' && subscription ? (
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-md-8">
                <div className="card shadow-lg border-0" style={{ borderRadius: '15px' }}>
                  <div className="card-body p-5">
                    <div className="text-center mb-4">
                      <div className="mb-3">
                        <i className="fa fa-check-circle text-success" style={{ fontSize: '60px' }}></i>
                      </div>
                      <h3 className="text-success mb-2">Active Subscription</h3>
                      <div className="badge bg-primary fs-6 mb-3">{subscription.status}</div>
                    </div>

                    <div className="subscription-details">
                      <div className="row mb-4">
                        <div className="col-md-6">
                          <h5 className="mb-3" style={{ color: '#2b61b1' }}>
                            <i className="fa fa-crown me-2"></i>
                            {subscription.plan?.plan_name || 'Subscription Plan'}
                          </h5>
                          <div className="mb-2">
                            <strong>Price:</strong> £{parseFloat(subscription.plan?.price || 0).toFixed(2)}/month
                          </div>
                          <div className="mb-2">
                            <strong>Student Limit:</strong> {subscription.plan?.student_limit || 'Unlimited'} students
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="mb-2">
                            <strong>Subscription ID:</strong> {subscription.stripe_subscription_id || 'N/A'}
                          </div>
                          <div className="mb-2">
                            <strong>Started:</strong> {subscription.current_period_start ? new Date(subscription.current_period_start).toLocaleDateString() : 'N/A'}
                          </div>
                          <div className="mb-2">
                            <strong>Next Billing:</strong> {subscription.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString() : 'N/A'}
                          </div>
                        </div>
                      </div>

                      <div className="border-top pt-3">
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0">
                              <i className="fa fa-puzzle-piece me-2"></i>
                              Active Add-ons
                            </h6>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={toggleAddonManagement}
                              style={{ 
                                borderColor: '#2b61b1', 
                                color: '#2b61b1',
                                backgroundColor: 'transparent'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#2b61b1';
                                e.target.style.color = 'white';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'transparent';
                                e.target.style.color = '#2b61b1';
                              }}
                            >
                              <i className={`fa ${managingAddons ? 'fa-times' : 'fa-edit'} me-1`}></i>
                              {managingAddons ? 'Cancel' : 'Manage Add-ons'}
                            </Button>
                          </div>

                          {managingAddons ? (
                            <div>
                              <style>{`
                                .form-check-input:checked {
                                  background-color: #2b61b1 !important;
                                  border-color: #2b61b1 !important;
                                }
                                .form-check-input:focus {
                                  border-color: #2b61b1 !important;
                                  box-shadow: 0 0 0 0.25rem rgba(78, 115, 223, 0.25) !important;
                                }
                              `}</style>
                              <div className="row mb-3">
                                {addons.filter(a => a.is_active).map((addon) => {
                                  const isCurrentlySubscribed = subscription.addons?.some(sa => sa.addon_id === addon.id);
                                  const isSelected = selectedAddonsToManage.includes(addon.id);
                                  return (
                                    <div key={addon.id} className="col-md-6 mb-2">
                                      <div className="form-check">
                                        <input
                                          className="form-check-input"
                                          type="checkbox"
                                          id={`manage-addon-${addon.id}`}
                                          checked={isSelected}
                                          onChange={() => handleAddonToggleForSubscription(addon.id)}
                                        />
                                        <label
                                          className="form-check-label d-flex justify-content-between align-items-center w-100"
                                          htmlFor={`manage-addon-${addon.id}`}
                                          style={{ cursor: 'pointer' }}
                                        >
                                          <span>{addon.addon_name}</span>
                                          <span className="badge" style={{ backgroundColor: '#2b61b1' }}>£{parseFloat(addon.price || 0).toFixed(0)}</span>
                                        </label>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                              <div className="d-flex justify-content-end gap-2">
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  onClick={toggleAddonManagement}
                                  disabled={updatingSubscription}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={handleUpdateSubscriptionAddons}
                                  disabled={updatingSubscription}
                                  style={{ backgroundColor: '#2b61b1', borderColor: '#2b61b1' }}
                                >
                                  {updatingSubscription ? (
                                    <>
                                      <Spinner animation="border" size="sm" className="me-2" />
                                      Updating...
                                    </>
                                  ) : (
                                    'Update Subscription'
                                  )}
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div>
                              {subscription.addons && subscription.addons.length > 0 ? (
                                <div className="row">
                                  {subscription.addons.map((addon) => (
                                    <div key={addon.id} className="col-md-6 mb-2">
                                      <div className="d-flex justify-content-between align-items-center">
                                        <span>{addon.addon?.addon_name || 'Addon'}</span>
                                        <span className="badge" style={{ backgroundColor: '#2b61b1' }}>£{parseFloat(addon.addon?.price || 0).toFixed(0)}</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-muted mb-0">No active add-ons</p>
                              )}
                            </div>
                          )}
                        </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Loading plans...</p>
          </div>
        ) : plans.length === 0 ? (
          <div className="text-center py-5">
            <p className="text-muted">No subscription plans available.</p>
          </div>
        ) : (
          <>
            <style>{`
              .form-check-input:checked {
                background-color: #2b61b1 !important;
                border-color: #2b61b1 !important;
              }
              .form-check-input:focus {
                border-color: #2b61b1 !important;
                box-shadow: 0 0 0 0.25rem rgba(78, 115, 223, 0.25) !important;
              }
              .form-check-input:checked {
                background-color: #2b61b1 !important;
                border-color: #2b61b1 !important;
              }
              .form-check-input:focus {
                border-color: #2b61b1 !important;
                box-shadow: 0 0 0 0.25rem rgba(78, 115, 223, 0.25) !important;
              }
            `}</style>
            {/* Plans Section */}
            <div className="position-relative mb-4">
              <div className="d-flex align-items-center">
                <button
                  className="btn btn-light border-0 shadow-sm me-2"
                  onClick={() => scrollPlans('left')}
                  style={{ minWidth: '40px', height: '40px', borderRadius: '50%' }}
                >
                  <i className="fa fa-chevron-left"></i>
                </button>
                <div
                  id="plans-scroll-container"
                  className="d-flex overflow-auto flex-nowrap flex-grow-1"
                  style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', paddingTop: '10px', paddingBottom: '10px', paddingLeft: '5px' }}
                >
                  <style>{`
                    #plans-scroll-container::-webkit-scrollbar {
                      display: none;
                    }
                  `}</style>
                  {plans.filter(p => p.is_active).map((plan) => (
                    <div
                      key={plan.id}
                      className="flex-shrink-0 me-3"
                      style={{ width: '320px' }}
                      onClick={() => handlePlanSelection(plan)}
                    >
                      <div
                        className={`card h-100 shadow-sm ${
                          selectedPlan?.id === plan.id ? '' : 'border-0'
                        }`}
                        style={{
                          borderRadius: '12px',
                          overflow: 'hidden',
                          cursor: 'pointer',
                          borderWidth: selectedPlan?.id === plan.id ? '3px' : '1px',
                          borderStyle: 'solid',
                          borderColor: selectedPlan?.id === plan.id ? '#2b61b1' : 'transparent',
                          transition: 'all 0.3s ease',
                          transform: selectedPlan?.id === plan.id ? 'scale(1.02)' : 'scale(1)'
                        }}
                      >
                        {selectedPlan?.id === plan.id && (
                          <div
                            className="position-absolute top-0 end-0 m-2 text-white rounded-circle d-flex align-items-center justify-content-center"
                            style={{ width: '30px', height: '30px', zIndex: 10, backgroundColor: '#2b61b1' }}
                          >
                            <i className="fa fa-check"></i>
                          </div>
                        )}
                        <div className="card-body d-flex flex-column p-4">
                          <h5 className="card-title fw-bold mb-2" style={{ color: '#333' }}>
                            {plan.plan_name}
                          </h5>
                          {plan.description && (
                            <p className="card-text text-muted small mb-3" style={{ minHeight: '60px' }}>
                              {plan.description}
                            </p>
                          )}
                          <div className="mt-auto">
                            <div className="d-flex align-items-baseline mb-2">
                              <span className="fw-bold" style={{ fontSize: '32px', color: '#2b61b1' }}>
                                £{parseFloat(plan.price || 0).toFixed(2)}
                              </span>
                              <span className="text-muted ms-1 small">/month</span>
                            </div>
                            <div className="d-flex align-items-center text-muted small mb-3">
                              <i className="fa fa-users me-2"></i>
                              <span>Up to {plan.student_limit} students</span>
                            </div>
                            {!isChangingExistingPlan && (
                              <>
                                <div className="d-flex align-items-center text-muted small mb-3">
                                  <i className="fa fa-pagelines me-2"></i>
                                  <span>14 Days Free Trial</span>
                                </div>
                                {addons.filter(a => a.is_active).length > 0 && (
                                  <div className="border-top pt-3 mt-2" onClick={(e) => e.stopPropagation()}>
                                    <p className="text-muted small mb-2 fw-bold">Add-ons:</p>
                                    {addons.filter(a => a.is_active).map((addon) => (
                                      <div key={addon.id} className="form-check mb-2" onClick={(e) => e.stopPropagation()}>
                                        <input
                                          className="form-check-input"
                                          type="checkbox"
                                          id={`addon-${plan.id}-${addon.id}`}
                                          checked={selectedPlan?.id === plan.id && selectedAddons.includes(addon.id)}
                                          disabled={selectedPlan?.id !== plan.id}
                                          onChange={(e) => {
                                            e.stopPropagation();
                                            handleAddonToggle(addon.id);
                                          }}
                                        />
                                        <label
                                          className="form-check-label small d-flex justify-content-between align-items-center w-100"
                                          htmlFor={`addon-${plan.id}-${addon.id}`}
                                          style={{ 
                                            cursor: selectedPlan?.id === plan.id ? 'pointer' : 'not-allowed',
                                            opacity: selectedPlan?.id === plan.id ? 1 : 0.6
                                          }}
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <span>{addon.addon_name}</span>
                                          <span className="fw-bold ms-2" style={{ color: '#2b61b1' }}>
                                            £{parseFloat(addon.price || 0).toFixed(0)}
                                          </span>
                                        </label>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  className="btn btn-light border-0 shadow-sm ms-2"
                  onClick={() => scrollPlans('right')}
                  style={{ minWidth: '40px', height: '40px', borderRadius: '50%' }}
                >
                  <i className="fa fa-chevron-right"></i>
                </button>
              </div>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {viewMode === 'details' ? (
          <>
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
            <Button variant="outline-primary" onClick={switchToPlansView}>
              <i className="fa fa-crown me-2"></i>
              Upgrade/Change Plan
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handlePurchase}
              disabled={
                !selectedPlan ||
                purchasing ||
                (isChangingExistingPlan &&
                  selectedPlan &&
                  (Number(subscription.plan_id ?? subscription.plan?.id) === Number(selectedPlan.id)))
              }
            >
              {purchasing ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Processing...
                </>
              ) : isChangingExistingPlan ? (
                `Update to ${selectedPlan ? selectedPlan.plan_name : 'plan'}`
              ) : (
                `Purchase ${selectedPlan ? selectedPlan.plan_name : 'Plan'}`
              )}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default SubscriptionPlansModal;
